import { OpsInfrastructureSourceState } from '../../../../../libs/api-client/src/generated/models';
import { useLocale } from '../../i18n/LocaleProvider';
import type { OpsLoadBalancer } from '../../lib/infrastructure-api';
import {
  formatBitRate,
  formatNumber,
  shortLabel,
} from '../../lib/infrastructure';
import { Badge } from '../Pill';
import { Section, Sparkline, Stat } from './InfrastructureParts';

/** The Hetzner Load Balancer(s): traffic now, the last hour of requests, and target health. */
export function LoadBalancerPanel({
  loadBalancers,
  state,
  nodePrefix,
}: {
  readonly loadBalancers: readonly OpsLoadBalancer[];
  readonly state: OpsInfrastructureSourceState;
  /** The shared node-name prefix, cut from target names. */
  readonly nodePrefix: string;
}) {
  const { t, locale } = useLocale();

  if (loadBalancers.length === 0) {
    return (
      <Section title={t('infrastructure.lb.title')}>
        <p className="text-sm text-gray-600">
          {state === OpsInfrastructureSourceState.NotConfigured
            ? t('infrastructure.lb.notConfigured')
            : t('infrastructure.lb.none')}
        </p>
      </Section>
    );
  }

  return (
    <>
      {loadBalancers.map((lb) => (
        <Section
          key={lb.name}
          title={t('infrastructure.lb.title')}
          lead={[lb.name, lb.type].filter(Boolean).join(' · ')}
        >
          <dl className="grid grid-cols-2 gap-3">
            <Stat
              label={t('infrastructure.lb.requests')}
              value={formatNumber(lb.requestsPerSecond, locale, 1)}
            />
            <Stat
              label={t('infrastructure.lb.connections')}
              value={formatNumber(lb.openConnections, locale)}
            />
            <Stat
              label={t('infrastructure.lb.newConnections')}
              value={formatNumber(lb.connectionsPerSecond, locale, 1)}
            />
            <Stat
              label={t('infrastructure.lb.bandwidth')}
              value={`↓ ${formatBitRate(lb.bandwidthInBps, locale)}`}
              sub={`↑ ${formatBitRate(lb.bandwidthOutBps, locale)}`}
            />
          </dl>
          <Sparkline
            title={t('infrastructure.lb.history')}
            points={lb.history.map((p) => ({
              t: p.t,
              value: p.requestsPerSecond,
            }))}
            format={(v) => formatNumber(v, locale, 1)}
          />
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-900">
              {t('infrastructure.lb.targets')}
            </h3>
            <ul className="divide-y divide-gray-100">
              {lb.targets.map((target) => (
                <li
                  key={target.name}
                  className="flex items-center justify-between gap-2 py-1.5 text-sm"
                >
                  <span className="truncate text-gray-900" title={target.name}>
                    {shortLabel(target.name, nodePrefix)}
                  </span>
                  <Badge
                    tone={
                      target.healthy === true
                        ? 'green'
                        : target.healthy === false
                          ? 'red'
                          : 'gray'
                    }
                  >
                    {target.healthy === true
                      ? t('infrastructure.lb.healthy')
                      : target.healthy === false
                        ? t('infrastructure.lb.unhealthy')
                        : t('infrastructure.lb.unknownHealth')}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        </Section>
      ))}
    </>
  );
}
